/**
 * Translates between the two ways of describing a client's access.
 *
 * What an admin thinks: "this partner reads members in the Ashanti Area, and maintains
 * the assemblies underneath it."
 * What the API needs: a permission entry per resource, plus a scope rule per dimension
 * of every one of those resources — around thirty objects for that one sentence.
 *
 * `buildSetup` goes from the sentence to the objects. `inferSetup` goes back, and is
 * deliberately strict: it rebuilds from its own guess and only returns a setup when the
 * result round-trips exactly. Anything hand-tuned in the advanced editors therefore
 * reports as "not representable" rather than being silently flattened.
 */

export const MEMBER_RESOURCE = 'member_profiles'

/** Reference lookups (member types, departments, …) are readable and never written. */
const READ_ACTIONS = ['CanList', 'CanView']

/**
 * A "location" is any resource the server marks as soft-deleting — which is exactly the
 * seven geography collections. Reading the flag rather than listing names keeps this in
 * step with the server registry if that set ever changes.
 */
export function isLocationResource(resource) {
  if (!resource || resource.resource === MEMBER_RESOURCE) return false
  if (resource.soft_delete) return true

  // Registry from an older server that does not report write semantics yet: fall back
  // to the structural signal, a location dimension on a non-member resource.
  return (resource.dimensions || []).some(dimension => dimension.matcher === 'location_tree')
}

export const ACCESS_LEVELS = [
  {
    value: 'read',
    label: 'Read only',
    hint: 'List, view and search member records. Cannot change anything.',
    recommended: true,
    member_actions: ['CanList', 'CanView'],
  },
  {
    value: 'write',
    label: 'Read and update',
    hint: 'Everything above, plus creating new member records and updating existing ones.',
    member_actions: ['CanList', 'CanView', 'CanAddNew', 'CanUpdate'],
  },
  {
    value: 'full',
    label: 'Full management',
    hint: 'Everything above, plus deleting member records. Deletions are not reversible.',
    danger: true,
    member_actions: ['CanList', 'CanView', 'CanAddNew', 'CanUpdate', 'CanDelete'],
  },
]

/**
 * The same three steps for the church's places — regions, areas, districts, assemblies,
 * mission blocks, RMCs and countries.
 *
 * Read is the right answer for almost every partner: they need to turn an `area_id`
 * into "Ashanti Area", not to redraw the church's map. The two write levels exist for
 * the partners who genuinely maintain a branch of the hierarchy on PAC's behalf.
 */
export const LOCATION_ACCESS_LEVELS = [
  {
    value: 'read',
    label: 'Read only',
    hint: 'Can list regions, areas, districts and assemblies so ids resolve to names. Cannot change the map.',
    recommended: true,
    location_actions: ['CanList', 'CanView'],
  },
  {
    value: 'write',
    label: 'May add and update places',
    hint: 'Creates new places and corrects existing ones, inside the places chosen above. Cannot remove anything.',
    location_actions: ['CanList', 'CanView', 'CanAddNew', 'CanUpdate'],
  },
  {
    value: 'full',
    label: 'May also remove places it added',
    hint: 'Everything above, plus removing places. Only places this client created itself, and only while nothing else refers to them.',
    danger: true,
    location_actions: ['CanList', 'CanView', 'CanAddNew', 'CanUpdate', 'CanDelete'],
  },
]

export const DEFAULT_SETUP = {
  // "everywhere" is never the default — a new client starts scoped to nothing until an
  // admin says otherwise, matching deny-by-default on the server.
  scope_mode: 'places',
  places: [],
  access: 'read',
  locations: 'read',
}

function levelFor(levels, value) {
  return levels.find(level => level.value === value) || levels[0]
}

function wantedActions(resource, access, location_access) {
  if (resource.resource === MEMBER_RESOURCE) return access.member_actions
  if (isLocationResource(resource)) return location_access.location_actions
  return READ_ACTIONS
}

function actionsForResource(resource, access, location_access) {
  const supported = resource.supported_actions || []
  return wantedActions(resource, access, location_access).filter(action => supported.includes(action))
}

/**
 * The sentence -> the objects.
 *
 * Geography and reference resources always get at least read access, because a partner
 * that can list members but cannot resolve an `area_id` into "Ashanti Area" has data it
 * cannot use. Non-location dimensions are set to "all" explicitly rather than left
 * absent, since an absent rule denies on the server.
 */
export function buildSetup(resources, setup) {
  const access = levelFor(ACCESS_LEVELS, setup.access)
  const location_access = levelFor(LOCATION_ACCESS_LEVELS, setup.locations)
  const restricted = setup.scope_mode === 'places'
  const places = restricted ? (setup.places || []).map(String) : []

  const permissions = []
  const scope_rules = []
  const entries = resources || []

  entries.forEach(resource => {
    const actions = actionsForResource(resource, access, location_access)
    if (!actions.length) return

    permissions.push({ resource: resource.resource, actions })

    const dimensions = resource.dimensions || []

    dimensions.forEach(dimension => {
      const is_location = dimension.matcher === 'location_tree'

      scope_rules.push({
        resource: resource.resource,
        dimension_key: dimension.key,
        mode: is_location && restricted ? 'restricted' : 'all',
        values: is_location && restricted ? [...places] : [],
      })
    })
  })

  return { permissions, scope_rules }
}

/** Order-insensitive shape comparison, so a reordered payload still round-trips. */
function fingerprint(payload) {
  const permissions = (payload.permissions || [])
    .map(entry => ({
      resource: entry.resource,
      actions: [...(entry.actions || [])].sort(),
    }))
    .sort((a, b) => a.resource.localeCompare(b.resource))

  const scope_rules = (payload.scope_rules || [])
    .map(rule => ({
      resource: rule.resource,
      dimension_key: rule.dimension_key,
      mode: rule.mode,
      values: (rule.values || []).map(String).sort(),
    }))
    .sort((a, b) => {
      const left = `${a.resource}.${a.dimension_key}`
      const right = `${b.resource}.${b.dimension_key}`
      return left.localeCompare(right)
    })

  return JSON.stringify({ permissions, scope_rules })
}

function locationDimensionKey(resources, resource_name) {
  const resource = (resources || []).find(entry => entry.resource === resource_name)
  if (!resource) return null

  const dimension = (resource.dimensions || []).find(
    entry => entry.matcher === 'location_tree'
  )

  return dimension ? dimension.key : null
}

/** The granted action set for one resource, as a comparable string. */
function grantedSignature(current, resource_name) {
  const granted = (current.permissions || []).find(entry => entry.resource === resource_name)
  return granted ? [...(granted.actions || [])].sort().join(',') : null
}

/**
 * The objects -> the sentence, or `null` when they do not form one.
 *
 * Returning `null` is the honest answer for a hand-tuned client, and is what keeps the
 * simple view from quietly discarding rules it cannot show.
 */
export function inferSetup(resources, current) {
  if (!resources || !resources.length) return null

  const member_resource = resources.find(entry => entry.resource === MEMBER_RESOURCE)
  if (!member_resource) return null

  const member_signature = grantedSignature(current, MEMBER_RESOURCE)
  if (member_signature === null) return null

  const read_only = levelFor(LOCATION_ACCESS_LEVELS, 'read')
  const memberSignatureFor = level => actionsForResource(member_resource, level, read_only)
    .sort()
    .join(',')

  const access = ACCESS_LEVELS.find(level => memberSignatureFor(level) === member_signature)
  if (!access) return null

  // The location level is read off the first location resource the registry lists; the
  // round-trip check below is what proves the rest agree with it.
  const location_resource = resources.find(isLocationResource)
  let locations = 'read'

  if (location_resource) {
    const location_signature = grantedSignature(current, location_resource.resource)
    if (location_signature === null) return null

    const locationSignatureFor = level => actionsForResource(location_resource, access, level)
      .sort()
      .join(',')

    const location_level = LOCATION_ACCESS_LEVELS.find(
      level => locationSignatureFor(level) === location_signature,
    )
    if (!location_level) return null

    locations = location_level.value
  }

  const dimension_key = locationDimensionKey(resources, MEMBER_RESOURCE)
  const location_rule = (current.scope_rules || []).find(
    rule => rule.resource === MEMBER_RESOURCE && rule.dimension_key === dimension_key
  )

  const candidate = {
    scope_mode: location_rule && location_rule.mode === 'restricted' ? 'places' : 'everywhere',
    places: location_rule ? (location_rule.values || []).map(String) : [],
    access: access.value,
    locations,
  }

  // The guess only counts if regenerating from it reproduces the payload exactly.
  return fingerprint(buildSetup(resources, candidate)) === fingerprint(current)
    ? candidate
    : null
}

/** Counts and flags for the "here is what this generates" line under the simple view. */
export function summarizeSetup(resources, setup) {
  const built = buildSetup(resources, setup)

  return {
    resources: built.permissions.length,
    rules: built.scope_rules.length,
    writes: setup.access !== 'read',
    deletes: setup.access === 'full',
    location_writes: setup.locations !== 'read',
    location_deletes: setup.locations === 'full',
  }
}
